/**
 * Prazo de fluxo (decisão 12 do Igor): quanto tempo, em dias úteis, um
 * ambiente leva da VENDA ao CARREGAMENTO. É o caminho inteiro (projeto,
 * aprovação, compras, fábrica, expedição), não só a marcenaria: por isso uma
 * venda pode caber na capacidade e ainda assim ter prazo improvável.
 *
 * Mostrado como p25 / mediana / p75, com o p75 destacado como "prazo seguro"
 * (3 em cada 4 ambientes saíram dentro dele). Recalculado do histórico das
 * últimas 52 semanas, nunca fixo.
 */
import { duEntre, ehIsoValido, somarDiasCorridos, somarDu } from "./calendario";
import { percentil } from "./precisao";

/** Abaixo disso um percentil é um punhado de ambientes, não "o normal". */
export const AMOSTRA_MINIMA_FLUXO = 30;
export const SEMANAS_DO_FLUXO = 52;

const DIAS_POR_SEMANA = 7;

function inicioDaJanela(hoje) {
  return somarDiasCorridos(hoje, -SEMANAS_DO_FLUXO * DIAS_POR_SEMANA);
}

function daFabrica(linha) {
  return !linha.servico && !linha.cancelado;
}

/**
 * O histórico do backend (`TerminoLegado`) guarda o término EFETIVO, sem dizer
 * se ele foi ajustado. Todo ajuste põe o término no próprio carregamento
 * (`terminoEfetivo`), então término = carregamento ali é tratado como ajuste
 * presumido: perde-se a rara folga 0 verdadeira, mas a mediana não é puxada
 * para baixo pelas cópias antigas (o erro fica do lado prudente: G maior).
 */
const AJUSTE_PRESUMIDO_NO_HISTORICO = "presumido_no_historico";

function ajusteDoHistorico(termino) {
  return ehIsoValido(termino.termino) && termino.termino === termino.carregamento ? AJUSTE_PRESUMIDO_NO_HISTORICO : null;
}

/**
 * União do extrato atual com os términos do backend, por `identidade`. O
 * extrato VENCE (mesma regra de `unirTerminos`): se a identidade está na
 * planilha de agora, vale o que ela diz, inclusive "sem carregamento".
 * Retorna `[{identidade, data_venda, termino, carregamento, ajuste}]`.
 */
function registrosDaUniao(linhas, terminos) {
  const decididas = new Set();
  const porIdentidade = new Map();
  (linhas || []).forEach((linha) => {
    if (linha.identidade) decididas.add(linha.identidade);
    if (!daFabrica(linha)) return;
    const identidade = linha.identidade || `sem-chave:${linha.linha_planilha}`;
    porIdentidade.set(identidade, {
      identidade,
      data_venda: linha.data_venda,
      termino: linha.termino,
      carregamento: linha.carregamento,
      ajuste: linha.ajuste_termino || null,
    });
  });
  (terminos || []).forEach((termino) => {
    if (!termino?.identidade || decididas.has(termino.identidade)) return;
    porIdentidade.set(termino.identidade, {
      identidade: termino.identidade,
      data_venda: termino.data_venda,
      termino: termino.termino,
      carregamento: termino.carregamento,
      ajuste: ajusteDoHistorico(termino),
    });
  });
  return [...porIdentidade.values()];
}

function carregadoNaJanela(registro, hoje) {
  const { carregamento } = registro;
  return ehIsoValido(carregamento) && carregamento >= inicioDaJanela(hoje) && carregamento <= hoje;
}

/** Percentil arredondado para cima: o prazo nunca sai menor do que o medido. */
function percentilParaCima(valores, p) {
  return Math.ceil(percentil(valores, p) - 1e-9);
}

/**
 * `prazoDeFluxo({linhas, terminos, naoUteis, hoje})`:
 * - amostra: ambientes da fábrica (sem serviço e cancelado) com venda e
 *   carregamento válidos, carregados nas últimas 52 semanas, carregamento ≥ venda;
 * - `n < 30` → `{suficiente:false, n}`;
 * - senão `{suficiente:true, n, p25, p50, p75, seHojeAssinar:{p25, p50, p75}}`,
 *   em dias úteis da unidade (as datas somam esses du a `hoje`).
 *
 * `linhas` = linhas do extrato atual; `terminos` = `TerminoLegado` do
 * backend (com `data_venda` e `carregamento`).
 */
export function prazoDeFluxo({ linhas, terminos, naoUteis, hoje } = {}) {
  const duracoes = registrosDaUniao(linhas, terminos)
    .filter(
      (r) => carregadoNaJanela(r, hoje) && ehIsoValido(r.data_venda) && r.carregamento >= r.data_venda,
    )
    .map((r) => duEntre(r.data_venda, r.carregamento, naoUteis));
  const n = duracoes.length;
  if (n < AMOSTRA_MINIMA_FLUXO) return { suficiente: false, n };
  const p25 = percentilParaCima(duracoes, 0.25);
  const p50 = percentilParaCima(duracoes, 0.5);
  const p75 = percentilParaCima(duracoes, 0.75);
  return {
    suficiente: true,
    n,
    p25,
    p50,
    p75,
    seHojeAssinar: {
      p25: somarDu(hoje, p25, naoUteis),
      p50: somarDu(hoje, p50, naoUteis),
      p75: somarDu(hoje, p75, naoUteis),
    },
  };
}

/**
 * `folgaTerminoCarregamento({linhas, terminos, naoUteis, hoje})` →
 * `{G, n, suficiente}`: mediana (para cima) de du entre o término da
 * marcenaria e o carregamento, nas últimas 52 semanas. É quanto antes da
 * entrega a marcenaria precisa terminar (acabamento, limpeza, expedição).
 *
 * Linhas da planilha com término AJUSTADO ficam de fora: nelas o término é
 * o próprio carregamento (esqueceram de lançar), e a folga 0 puxaria a
 * mediana para baixo sem ser real. Do histórico do backend, que não guarda o
 * ajuste, sai todo término igual ao carregamento (`ajusteDoHistorico`). Com
 * menos de 30 pares, `G = 0`.
 */
export function folgaTerminoCarregamento({ linhas, terminos, naoUteis, hoje } = {}) {
  const folgas = registrosDaUniao(linhas, terminos)
    .filter(
      (r) =>
        !r.ajuste &&
        carregadoNaJanela(r, hoje) &&
        ehIsoValido(r.termino) &&
        r.carregamento >= r.termino,
    )
    .map((r) => duEntre(r.termino, r.carregamento, naoUteis));
  const n = folgas.length;
  if (n < AMOSTRA_MINIMA_FLUXO) return { G: 0, n, suficiente: false };
  return { G: percentilParaCima(folgas, 0.5), n, suficiente: true };
}
